/**
 * MODULE: platform/browser/frontend/hooks/useBrowserCapability.ts
 * PURPOSE: Check whether capability.browser is enabled for the current tenant.
 */

import { useQuery } from '@tanstack/react-query';
import { API_CONFIG, getAuthHeaders } from '@shared/lib/frontend/api/config';
import { useSessionAuthReady } from '@shared/lib/frontend/api/sessionAuthReady';

export const BROWSER_CAPABILITY_KEY = 'capability.browser';

type CapabilityCheckResponse = {
  capability_key: string;
  enabled: boolean;
  tenant_id?: string;
};

async function fetchBrowserCapability(): Promise<CapabilityCheckResponse> {
  const url = `${API_CONFIG.baseURL.replace(/\/$/, '')}/api/v1/features/${BROWSER_CAPABILITY_KEY}/enabled`;
  const response = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    return { capability_key: BROWSER_CAPABILITY_KEY, enabled: false };
  }

  return response.json();
}

export function useBrowserCapability() {
  const apiReady = useSessionAuthReady();
  return useQuery({
    queryKey: ['capability', BROWSER_CAPABILITY_KEY],
    queryFn: fetchBrowserCapability,
    enabled: apiReady,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function isBrowserCapabilityEnabled(
  data: CapabilityCheckResponse | undefined,
  isLoading: boolean,
): boolean {
  if (isLoading) return true;
  return data?.enabled !== false;
}
